import React, { useState, useRef } from 'react';
import { 
  Send, 
  UploadCloud, 
  X, 
  Image as ImageIcon, 
  FileText, 
  ClipboardPaste, 
  Zap,
  Lock,
  CornerDownLeft,
  Sparkles,
  Paperclip
} from 'lucide-react';

interface QuickDropProps {
  onPushText: (text: string) => Promise<void>;
  onPushFile: (fileDataUrl: string, fileName: string, fileMimeType: string) => Promise<void>;
  disabled: boolean;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

export const QuickDrop: React.FC<QuickDropProps> = ({ onPushText, onPushFile, disabled, textareaRef }) => {
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [stagedFile, setStagedFile] = useState<{
    dataUrl: string;
    name: string;
    type: string;
    size: number;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const triggerHaptic = () => {
    try {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(15);
      }
    } catch {}
  };

  const handleSendText = async () => {
    if (!inputText.trim() || isSending || disabled) return;
    setIsSending(true);
    triggerHaptic();
    try {
      await onPushText(inputText.trim());
      setInputText('');
    } catch (err) {
      console.error('Failed to broadcast text:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setInputText(text);
        triggerHaptic();
      }
    } catch (err) {
      console.error('Failed to read OS clipboard:', err);
    }
  };

  const handleProcessFile = (file: File) => {
    if (file.size > 25 * 1024 * 1024) {
      alert('File size exceeds 25MB direct mesh transfer limit.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setStagedFile({
        dataUrl: reader.result as string,
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size
      });
      triggerHaptic();
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleProcessFile(e.dataTransfer.files[0]);
    }
  };

  const handleSendStagedFile = async () => {
    if (!stagedFile || isSending || disabled) return;
    setIsSending(true);
    triggerHaptic();
    try {
      await onPushFile(stagedFile.dataUrl, stagedFile.name, stagedFile.type);
      setStagedFile(null);
    } catch (err) {
      console.error('Failed to send file:', err);
    } finally {
      setIsSending(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="native-glass rounded-2xl p-4 border border-white/10 shadow-2xl relative">
      {/* Top Composer Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-cyan-950/60 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
            <Zap size={14} />
          </div>
          <div>
            <h3 className="text-xs font-bold tracking-wider text-slate-200 uppercase font-display">
              Instant Composer & QuickDrop
            </h3>
            <p className="text-[10px] text-slate-400 font-mono">End-to-End Encrypted Relay Stream</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-[10px] font-mono text-cyan-300 bg-cyan-950/40 border border-cyan-500/30 px-2.5 py-0.5 rounded-full">
          <Lock size={11} />
          <span>AES-GCM-256</span>
        </div>
      </div>

      {/* Staged File Card */}
      {stagedFile && (
        <div className="p-3 rounded-xl bg-cyan-950/30 border border-cyan-500/40 mb-3 flex items-center justify-between gap-3 shadow-glow-cyan animate-in fade-in duration-150">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-cyan-900/40 border border-cyan-500/30 flex items-center justify-center flex-shrink-0 text-cyan-300">
              {stagedFile.type.startsWith('image/') ? <ImageIcon size={20} /> : <FileText size={20} />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-white truncate">{stagedFile.name}</p>
              <p className="text-[10px] text-cyan-300 font-mono">
                {stagedFile.type} • {formatBytes(stagedFile.size)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setStagedFile(null)}
              className="p-1.5 text-slate-400 hover:text-rose-400 rounded-lg hover:bg-white/5 transition-colors"
              title="Cancel file"
            >
              <X size={14} />
            </button>

            <button
              onClick={handleSendStagedFile}
              disabled={isSending || disabled}
              className="btn-primary text-xs py-1.5 px-3.5"
            >
              <Send size={12} />
              <span>{isSending ? 'Sending...' : 'Broadcast File'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Native Textarea + Drop Surface */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={`relative rounded-xl border transition-all ${
          isDragOver
            ? 'border-cyan-400 bg-cyan-950/40 shadow-glow-cyan scale-[1.01]'
            : 'border-white/10 bg-[#080A14] focus-within:border-cyan-500/50 shadow-inner'
        }`}
      >
        <textarea
          ref={textareaRef}
          rows={3}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              handleSendText();
            }
          }}
          placeholder="Paste or type text, code, URLs, or drag & drop files here to broadcast across all mesh devices..."
          className="w-full bg-transparent p-3.5 text-xs md:text-sm text-slate-100 placeholder-slate-500 outline-none resize-none leading-relaxed"
          disabled={disabled}
        />

        {/* Action Toolbar */}
        <div className="flex items-center justify-between p-2.5 bg-black/40 border-t border-white/5 rounded-b-xl">
          <div className="flex items-center gap-1.5">
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleProcessFile(e.target.files[0]);
                }
              }}
              className="hidden"
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="text-xs font-semibold text-slate-400 hover:text-cyan-300 flex items-center gap-1.5 px-2.5 py-1 rounded-lg hover:bg-white/5 transition-all cursor-pointer"
            >
              <Paperclip size={13} />
              <span>Attach</span>
            </button>

            <button
              type="button"
              onClick={handlePasteFromClipboard}
              disabled={disabled}
              className="text-xs font-semibold text-slate-400 hover:text-cyan-300 flex items-center gap-1.5 px-2.5 py-1 rounded-lg hover:bg-white/5 transition-all cursor-pointer hidden sm:flex"
            >
              <ClipboardPaste size={13} />
              <span>Paste Clipboard</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 font-mono hidden md:inline">
              Ctrl+Enter
            </span>

            <button
              type="button"
              onClick={handleSendText}
              disabled={!inputText.trim() || isSending || disabled}
              className="btn-primary text-xs py-1.5 px-4 disabled:opacity-30 disabled:cursor-not-allowed shadow-md shadow-cyan-500/20 cursor-pointer"
            >
              <Send size={12} />
              <span>{isSending ? 'Sending...' : 'Broadcast'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
